/**
 * componentLoader.ts
 *
 * Route-string helpers (getEncodedRoute, createTabRegistry) built on top of
 * generated-routes.json.
 *
 * CONSOLIDATED (2026-09-29): createComponentBySeq() used to maintain its OWN
 * independent copy of every RegisteredXxx import.meta.glob() folder registry,
 * duplicating componentResolver.ts's glob calls with a DIFFERENT (and
 * incomplete) folder list, plus a separate seq→route index built from
 * generated-routes.json instead of tsx-inventory-manifest.json. That
 * duplication is exactly why the same sequence could resolve successfully
 * from one entry point (SequenceRouteLoader, via this file) and fail from
 * another (a module's own tabs, via componentResolver.ts) whenever the two
 * folder lists drifted out of sync. createComponentBySeq now delegates
 * directly to resolveComponentBySeq() - ONE glob registry set, ONE manifest,
 * ONE resolution algorithm, shared by every entry point in the app.
 */

import React from 'react';
import generatedRoutes from '../generated-routes.json';
import { resolveComponentBySeq } from './componentResolver';

// Pre-index routes by sequence number at module load (O(1) lookups)
// Used ONLY by getEncodedRoute() below (route-STRING lookup for legacy
// cypher-encoded navigation) - NOT used for component resolution anymore.
const routesBySeq = new Map<number, any>();
(generatedRoutes as any).routes.forEach((route: any) => {
    if (route.seq) {
        routesBySeq.set(route.seq, route);
    }
});

/**
 * Create a lazy component loader by sequence number.
 *
 * Thin delegate to componentResolver.ts's resolveComponentBySeq() - the
 * single canonical resolution path (manifest lookup + glob registry + lazy +
 * chunk-retry) shared by every caller, whether resolving a top-level route
 * (this function, via SequenceRouteLoader) or an in-module tab
 * (resolveComponentBySeq() called directly).
 *
 * Usage:
 *   const MyTab = createComponentBySeq(10039);
 *   <Suspense fallback={...}><MyTab /></Suspense>
 */
export function createComponentBySeq(
    seq: number,
): React.LazyExoticComponent<React.ComponentType<any>> {
    return resolveComponentBySeq(seq);
}

/**
 * Get the encoded (cyphered) route for navigation.
 * Returns the route with the security key included.
 *
 * Usage:
 *   const route = getEncodedRoute(10010); // '/TGC.00M.2A-00059-07-31-4-TAB'
 *   navigate(route);
 */
export function getEncodedRoute(seq: number): string {
    const route = routesBySeq.get(seq);

    if (!route) {
        throw new Error(
            `Component ${seq} not found in build manifest (generated-routes.json). ` +
                `Run: npm run sync-manifests`,
        );
    }

    if (!route.directRoute) {
        throw new Error(
            `Component ${seq} has no directRoute in manifest. ` +
                `This indicates a stale manifest. Run: npm run sync-manifests`,
        );
    }

    // ✅ Phase 5c: Fail-fast, no fallback
    // Pre-resolution architecture: Route must be in manifest
    return route.directRoute;
}

/**
 * Bulk create components for tab registry.
 *
 * Usage:
 *   const TABS = createTabRegistry([
 *     { seq: 10039, label: 'Auth Tiers' },
 *     { seq: 10374, label: 'Directory CRUD' },
 *   ]);
 */
export function createTabRegistry(
    tabs: Array<{ seq: number; label: string }>,
): Array<{ seq: number; label: string; component: React.LazyExoticComponent<any> }> {
    return tabs.map(({ seq, label }) => ({
        seq,
        label,
        component: createComponentBySeq(seq),
    }));
}
